import { z } from 'zod';
import { Field, Validation } from '../sceneDataSchema';
import { dateValidator } from './validators/DateValidator';
import { booleanValidator } from './validators/BooleanValidator';
import { numberValidator } from './validators/NumberValidator';
import { stringValidator } from './validators/StringValidator';
import { DataType, ValidationType } from '@/constants/validationTypes';

export interface EntityPath {
  scope: string;
  attribute: string;
  entity_index: number;
}

export function createSchema(inputFields: Field[]) {
  return z.object(
    inputFields.reduce<Record<string, ReturnType<typeof createSchemaForComponent>>>((schemaShape, component) => {
      schemaShape[encodeFieldKey(component)] = createSchemaForComponent(component);
      return schemaShape;
    }, {})
  );
}

function createSchemaForComponent(field: Field) {
  const dataType = extractDataType(field);
  const validations = field.validation_rules;

  switch (dataType) {
    case DataType.String:
      return stringValidator(validations);
    case DataType.Integer:
      return numberValidator(validations);
    case DataType.Boolean:
      return booleanValidator(validations);
    case DataType.Date:
      return dateValidator(validations);
    default:
      // Invalid type "never" of template literal expression.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unsupported data type: ${dataType}`);
  }
}

/**
 * React form hooks needs a unique string name and we need a consistent way to encode these.
 * We encode the entity triplet (scope, entity_index, attribute) as "scope::part/entity_index/attribute".
 * Dots in scope are replaced with "::" so react-hook-form doesn't interpret them as nested paths.
 * @param param0 The entity triplet we want to encode
 * @returns The encoded field key string
 */
export function encodeFieldKey({ scope, entity_index, attribute }: EntityPath): string {
  return `${scope.split('.').join('::')}/${entity_index}/${attribute}`;
}

/**
 * Decodes a field key from `encodeFieldKey` back into the entity triplet it was encoded with.
 * @param key An encoded field key string
 * @returns An EntityPath which represents the triplet of data
 */
export function decodeFieldKey(key: string): EntityPath {
  const [rawScope, indexStr, attribute] = key.split('/');

  if (!rawScope || !attribute || indexStr === undefined) {
    throw new Error('Unable to parse encoded key');
  }

  const entity_index = Number(indexStr);
  if (Number.isNaN(entity_index)) {
    throw new Error('Unable to parse encoded key');
  }

  return {
    scope: rawScope.split('::').join('.'),
    entity_index,
    attribute,
  };
}

/**
 * React form hooks needs unique string name for each field it is modeling. When
 * we need to gather all the information for a multiple drivers or vehicles we run into issues
 * where the keys then become non unique
 *
 * To solves this we now encode a triplet together (scope, attribute, entity_index). This method
 * takes this encoded triplet, decodes it the generates the appropriate nested structure for submission
 * which is now
 *  { "<scope>": { "<entity_index>": { "<attribute1>": "data1", "<attributeN>": "dataN"} } }
 * @param formData This is the encoded form data where our triplet is encoded
 * @returns The structure data to patch to the agency api
 */
export const decodeSubmission = (formData: Record<string, unknown>) => {
  const decoded: Record<string, Record<string, Record<string, unknown>>> = {};

  Object.entries(formData).forEach(([encodedKey, value]) => {
    const { entity_index, scope, attribute } = decodeFieldKey(encodedKey);

    decoded[scope] ||= {};
    decoded[scope][entity_index.toString()] ||= {};
    decoded[scope][entity_index.toString()][attribute] = value;
  });

  return decoded;
};

export function extractDataTypeFromRules(rules: Validation[]): DataType {
  for (const rule of rules) {
    if (rule.type === ValidationType.DataType) {
      return rule.value;
    }
  }

  throw new Error('Data type not specified in validation rules');
}

export function extractDataType(field: Field): DataType {
  return extractDataTypeFromRules(field.validation_rules);
}
